/**
 * Web's API surface — bridges to @grove/api-client.
 *
 * Two ways to call the backend from web code:
 *
 * 1. `apiFetch<T>(path, init)` — the original untyped wrapper. Existing call
 *    sites keep working. Preferred for one-off paths the typed client doesn't
 *    cover yet, but most paths now have a typed equivalent.
 *
 * 2. `api.listArticles()` etc. — typed methods from the shared @grove/api-client.
 *    Returns typed Articles, throws ApiError/AuthError, used by all consumers
 *    (web, mobile, browser extension). Prefer this for new code.
 */

import { createApiClient, ApiError, AuthError } from '@grove/api-client';

const API = import.meta.env.VITE_API_URL ?? 'https://api.grovereader.com';

// AuthContext registers a handler at mount; both `api` and `apiFetch` invoke
// it on 401 so we don't have to thread the callback through every call site.
let onAuthErrorCb: (() => void) | null = null;
export function setAuthErrorHandler(cb: (() => void) | null) {
  onAuthErrorCb = cb;
}

export const api = createApiClient({
  baseUrl: API,
  // Browser cookies handle auth — Better Auth sets a session cookie that
  // travels with `credentials: 'include'`.
  fetch: (url, init) => fetch(url, { ...init, credentials: 'include' }),
  onAuthError: () => onAuthErrorCb?.(),
});

// Legacy wrapper kept for call sites that haven't migrated to typed methods.
// New code should prefer `api.foo()` for type safety.
export async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API}/api${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...init?.headers },
    ...init,
  });
  if (res.status === 401) {
    onAuthErrorCb?.();
    throw new AuthError(`API 401: ${path}`);
  }
  if (!res.ok) throw new ApiError(res.status, `API ${res.status}: ${path}`);
  return res.json() as Promise<T>;
}

// Re-export types + errors so callers can `import { Article, ApiError } from '../lib/api'`.
export type {
  Article,
  ArticleStatus,
  DiscoverItem,
  ExtractionStatus,
  Highlight,
  NewTokenResponse,
  Session,
  Token,
  User,
  UserProfile,
} from '@grove/api-client';
export { ApiError, AuthError, statusConfig } from '@grove/api-client';
